/* Test cases for relative urls:
valid:
/
/about
/contact-us
/products/item1
/services/service-123
/2024/04/sample-post
/files/download/setup.exe
/user/profile
/search?q=books
/path/to/resource/with/longer/path
/assets/images/logo.png
/en-us/default.aspx
/docs/guide/en/part-1
/archive/2024/03/
/temp%20files/
/category/subcategory/page.html
/feed.xml
/path-with-dash/
/path_with_underscore/
/percent%20encoded%20space

not valid:
http://www.example.com/about
https://example.com/contact
//cdn.example.com/lib.js 
/about us/ 
?name=value
#section
mailto:example@example.com
javascript:alert('XSS')
./../
C:\Directory\file.txt
/path/to/url/with/"quotes"
/path/to/url/with/<tags>
 /leading/spaces 
/trailing/spaces 
//
//protocal.com/relative/url
@file
/\evil.com                (backslash: WHATWG URL treats \ as /, so this resolves cross-origin to //evil.com)
/\/evil.com
/\@evil.com
*/

// A safe root-relative URL: one leading slash, not protocol-relative, and containing nothing
// that would let it escape the embedding page's origin once resolved with `new URL(value, base)`.
// WHATWG URL parsing treats `\` like `/` for http(s) schemes, so `/\evil.com` would resolve to
// https://evil.com/ while looking same-site — a plain `^/(?!/)` pattern accepts it. So backslashes
// and control characters are rejected, and the resolved origin is proven rather than assumed.
//
// Keep in sync with `isRootRelative` in
// webng/liveblog-mcp-server/src/content/embedInput.ts: the two are the reader and writer ends of
// the same web-embed contract — the MCP server persists a relative URL unresolved, this widget
// resolves it against the page origin — and they must classify a value identically.
const ROOT_RELATIVE = /^\/(?![/\\])[^\s\\]*$/;
const PLACEHOLDER_ORIGIN = "https://placeholder.invalid";

function hasControlCharacter(value: string): boolean {
	for (const cp of value) {
		const code = cp.codePointAt(0) ?? 0;
		if (code < 0x20 || code === 0x7f) return true;
	}
	return false;
}

export function isRelativeUrl(url: string): boolean {
	if (!ROOT_RELATIVE.test(url) || hasControlCharacter(url)) return false;
	try {
		return new URL(url, `${PLACEHOLDER_ORIGIN}/`).origin === PLACEHOLDER_ORIGIN;
	} catch {
		return false;
	}
}
